/**
 * Confirmation modal to delete an organisation.
 *
 * Extracted from OrganisationManagementPage.tsx (Sub-PR 6.3).
 */

import { useTranslation } from 'react-i18next'
import { Loader2, Trash2 } from 'lucide-react'
import type { Organisation } from '../../types'

interface DeleteOrganisationModalProps {
  org: Organisation
  formError: string | null
  formLoading: boolean
  onClose: () => void
  onConfirm: () => void
}

export function DeleteOrganisationModal({
  org,
  formError,
  formLoading,
  onClose,
  onConfirm,
}: DeleteOrganisationModalProps): React.ReactElement {
  const { t } = useTranslation('admin')
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-stone-800">
        <div className="p-6">
          <div className="mb-4 flex items-center gap-4">
            <div className="rounded-full bg-red-100 p-3 dark:bg-red-900/30">
              <Trash2 className="h-6 w-6 text-red-600 dark:text-red-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-stone-900 dark:text-stone-100">
                {t('organisations.deleteOrganisation')}
              </h2>
              <p className="text-sm text-stone-500 dark:text-stone-400">
                {t('organisations.deleteWarning')}
              </p>
            </div>
          </div>
          <p className="mb-6 text-stone-700 dark:text-stone-300">
            {t('organisations.deleteConfirmation', { name: org.name })}
          </p>
          {formError && (
            <div className="mb-4 rounded-lg bg-red-50 p-3 text-sm text-red-600 dark:bg-red-900/20 dark:text-red-400">
              {formError}
            </div>
          )}
          <div className="flex justify-end gap-3">
            <button
              onClick={onClose}
              className="rounded-lg px-4 py-2 text-stone-700 hover:bg-stone-100 dark:text-stone-300 dark:hover:bg-stone-700"
            >
              {t('actions.cancel')}
            </button>
            <button
              onClick={onConfirm}
              disabled={formLoading}
              className="flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-white hover:bg-red-700 disabled:opacity-50"
            >
              {formLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4" />
              )}
              {t('actions.delete')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
